'use client';

import { useEffect } from 'react';
import { ATTRIBUTION_COOKIE } from '@/lib/attribution/constants';

// Fires once per session, on the first page the visitor lands on, when no
// ffl_attr cookie exists yet. Sends the URL's UTM params + document.referrer
// to /api/attribution/track which writes the first-touch cookie and inserts a
// click row.
//
// If the visitor came through /r/<code>, the redirect route already set the
// cookie - this component is a no-op for that path.

function hasAttributionCookie(): boolean {
  if (typeof document === 'undefined') return true;
  return document.cookie.split('; ').some((c) => c.startsWith(`${ATTRIBUTION_COOKIE}=`));
}

// Staff browsers are skipped: a staff member opening the site is not a
// prospect. The server routes also refuse a browser carrying ffl_no_track.
export function AttributionCapture({ skip = false }: { skip?: boolean }) {
  useEffect(() => {
    if (skip || hasAttributionCookie()) return;
    const url = new URL(window.location.href);
    const payload = {
      utm_source: url.searchParams.get('utm_source'),
      utm_medium: url.searchParams.get('utm_medium'),
      utm_campaign: url.searchParams.get('utm_campaign'),
      utm_content: url.searchParams.get('utm_content'),
      referrer: document.referrer || null,
      landing_path: url.pathname || '/',
    };
    fetch('/api/attribution/track', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Non-blocking - attribution failures must never affect the user.
    });
  }, [skip]);
  return null;
}
